import React, { useEffect } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  Paper,
} from "@mui/material";
import { formatFileSize } from "../../services/Utils";
import Constants, { IMAGE_UPLOAD_ERRORS } from "../../services/Constants";
import uploadFileUsingS3Api from "../../services/AwsFileUploader.js";
import { updateCognitoAiUploadStatus } from "../../services/CognitoAi";

export default function CognitoAiFileUploadProgressPane({
  file,
  uploadMetadata,
  stsToken,
  cognitoAiBucket,
  onComplete,
  onFailed,
  uploadProgress,
  setUploadProgress,
  setUploadStatus,
  uploadInstance,
  setUploadInstance,
}) {
  const uploadStartedRef = React.useRef(false);
  const { UPLOAD_STATUS: UploadStatus } = Constants;

  const updateCognitoUploadStatus = async (uploadId, status) => {
    try {
      await updateCognitoAiUploadStatus({
        uploadId,
        uploadStatus: status,
      });
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  useEffect(() => {
    // Ensure upload only starts once
    if (uploadStartedRef.current) return;
    uploadStartedRef.current = true;

    const startUpload = async () => {
      try {
        setUploadStatus(UploadStatus.InProgress);

        const result = await uploadFileUsingS3Api({
          bucket: cognitoAiBucket,
          key: uploadMetadata.uploadId + "/" + uploadMetadata.uploadId,
          selectedFile: file,
          onInit: (instance) => {
            setUploadInstance?.(instance || null);
          },
          onProgress: (progress) => {
            if (progress?.part === 2) {
              updateCognitoUploadStatus(
                uploadMetadata.uploadId,
                UploadStatus.InProgress,
              );
            }
            setUploadProgress({
              loaded: progress.loaded || 0,
              total: progress.total || file.size,
            });
          },
          creds: stsToken,
        });

        if (result) {
          setUploadStatus(UploadStatus.Completed);
          await updateCognitoUploadStatus(
            uploadMetadata.uploadId,
            UploadStatus.Completed,
          );
          onComplete({ success: true, uploadId: uploadMetadata.uploadId });
        }
      } catch (error) {
        console.error("Upload failed:", error);
        const { message = IMAGE_UPLOAD_ERRORS.SOMETHING_WENT_WRONG } = error;
        await uploadInstance?.abort?.();
        setUploadStatus(UploadStatus.Failed);
        onFailed(message || "Upload failed. Please try again.");
      }
    };

    startUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProgressPercentage = () => {
    if (uploadProgress.total === 0) return 0;
    return Math.round((uploadProgress.loaded / uploadProgress.total) * 100);
  };

  return (
    <Box sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4, textAlign: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <CircularProgress size={60} />
        </Box>

        <Typography variant="h6" gutterBottom>
          Uploading {file.name}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please wait while we upload your file...
        </Typography>

        <Box sx={{ width: "100%", mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={getProgressPercentage()}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>

        <Typography variant="h4" color="primary" gutterBottom>
          {getProgressPercentage()}%
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {formatFileSize(uploadProgress.loaded)} of{" "}
          {formatFileSize(uploadProgress.total)}
        </Typography>
      </Paper>
    </Box>
  );
}
